import { useState, useEffect } from 'react';
import { mcqAPI, responseAPI } from '../../utils/api';
import { toast } from 'react-toastify';
import {
  RiArrowLeftLine, RiQuestionLine, RiUserLine,
  RiCheckLine, RiCloseLine, RiBarChartLine,
} from 'react-icons/ri';

const StatCard = ({ label, value, color }) => {
  const colors = {
    blue:    'bg-blue-50 border-blue-100 text-blue-600',
    green:   'bg-emerald-50 border-emerald-100 text-emerald-600',
    red:     'bg-red-50 border-red-100 text-red-600',
    indigo:  'bg-violet-50 border-violet-100 text-violet-600',
  };
  return (
    <div className={`rounded-xl border p-5 text-center ${colors[color] || colors.blue}`}>
      <p className="text-2xl font-bold leading-none">{value}</p>
      <p className="text-xs text-slate-500 font-medium mt-1">{label}</p>
    </div>
  );
};

const ResponseManagement = () => {
  const [mcqs, setMcqs] = useState([]);
  const [selectedMcq, setSelectedMcq] = useState(null);
  const [responses, setResponses] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchMCQs(); }, []);

  const fetchMCQs = async () => {
    try {
      const response = await mcqAPI.getAllMCQs();
      setMcqs(response.data.mcqs || []);
    } catch {
      toast.error('Failed to load MCQs');
    } finally {
      setLoading(false);
    }
  };

  const handleMCQSelect = async (mcqId) => {
    setSelectedMcq(mcqId);
    setLoading(true);
    try {
      const [responsesRes, statsRes] = await Promise.all([
        responseAPI.getResponsesByMCQ(mcqId),
        responseAPI.getMCQStatistics(mcqId),
      ]);
      setResponses(responsesRes.data.responses || []);
      setStatistics(statsRes.data.statistics);
    } catch {
      toast.error('Failed to load responses');
    } finally {
      setLoading(false);
    }
  };

  /* ── MCQ selection grid ─────────────────────────────────────────────── */
  if (!selectedMcq) {
    return (
      <div className="p-6 max-w-6xl mx-auto animate-fade-in-up">
        <div className="mb-6">
          <h1 className="text-xl font-bold text-slate-800">Student Responses</h1>
          <p className="text-sm text-slate-500 mt-0.5">Select a test to view submissions</p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="w-8 h-8 spinner" />
          </div>
        ) : mcqs.length === 0 ? (
          <div className="card p-12 text-center">
            <RiQuestionLine className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 font-medium">No MCQ tests found</p>
            <p className="text-slate-400 text-sm mt-1">Create an MCQ test first</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 stagger">
            {mcqs.map(mcq => (
              <button
                key={mcq._id}
                onClick={() => handleMCQSelect(mcq._id)}
                className="card-hover p-5 text-left group animate-fade-in-up"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="w-9 h-9 bg-violet-100 rounded-xl flex items-center justify-center">
                    <RiQuestionLine className="w-4 h-4 text-violet-600" />
                  </div>
                  <span className={`badge ${mcq.isPublished ? 'badge-green' : 'badge-yellow'}`}>
                    {mcq.isPublished ? 'Published' : 'Draft'}
                  </span>
                </div>
                <h3 className="font-semibold text-slate-800 group-hover:text-blue-600 transition-colors mb-1 line-clamp-1">{mcq.title}</h3>
                {mcq.description && <p className="text-sm text-slate-500 line-clamp-2 mb-3">{mcq.description}</p>}
                <div className="flex items-center gap-3 text-xs text-slate-400 pt-3 border-t border-slate-100">
                  <span>{mcq.totalQuestions} questions</span>
                  <span>{mcq.duration} min</span>
                  <span className="ml-auto text-blue-600 font-semibold group-hover:translate-x-0.5 transition-transform inline-block">
                    View →
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  /* ── Responses view ─────────────────────────────────────────────────── */
  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-8 h-8 spinner" />
    </div>
  );

  const mcq = mcqs.find(m => m._id === selectedMcq);

  return (
    <div className="p-6 max-w-6xl mx-auto animate-fade-in-up">
      {/* Back + title */}
      <button
        onClick={() => { setSelectedMcq(null); setResponses([]); setStatistics(null); }}
        className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800 font-medium mb-5 transition-colors"
      >
        <RiArrowLeftLine className="w-4 h-4" /> Back to MCQs
      </button>

      <div className="mb-6">
        <h1 className="text-xl font-bold text-slate-800">{mcq?.title}</h1>
        <p className="text-sm text-slate-500 mt-0.5">Student submission results</p>
      </div>

      {/* Stats */}
      {statistics && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <StatCard label="Total Attempts" value={statistics.totalStudents} color="blue" />
          <StatCard label="Average Score"  value={`${statistics.averageScore}%`} color="indigo" />
          <StatCard label="Passed"          value={statistics.passedCount} color="green" />
          <StatCard label="Failed"          value={statistics.failedCount} color="red" />
        </div>
      )}

      {/* Table */}
      {responses.length === 0 ? (
        <div className="card p-12 text-center">
          <RiBarChartLine className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 font-medium">No responses yet</p>
          <p className="text-slate-400 text-sm mt-1">Students haven't submitted this test yet</p>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-5 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Student</th>
                  <th className="px-5 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Roll #</th>
                  <th className="px-5 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide hidden sm:table-cell">Class</th>
                  <th className="px-5 py-3.5 text-center text-xs font-semibold text-slate-500 uppercase tracking-wide">Correct</th>
                  <th className="px-5 py-3.5 text-center text-xs font-semibold text-slate-500 uppercase tracking-wide">Score</th>
                  <th className="px-5 py-3.5 text-center text-xs font-semibold text-slate-500 uppercase tracking-wide">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {responses.map(r => (
                  <tr key={r._id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-2.5">
                        <div className="w-7 h-7 bg-slate-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <RiUserLine className="w-3.5 h-3.5 text-slate-500" />
                        </div>
                        <span className="font-semibold text-slate-800">{r.studentName}</span>
                      </div>
                    </td>
                    <td className="px-5 py-3.5 text-slate-600 font-mono text-xs">{r.rollNumber}</td>
                    <td className="px-5 py-3.5 hidden sm:table-cell">
                      <span className="badge badge-slate">{r.className}</span>
                    </td>
                    <td className="px-5 py-3.5 text-center font-semibold text-emerald-600">
                      {r.correctAnswers}/{r.totalQuestions}
                    </td>
                    <td className="px-5 py-3.5 text-center font-bold text-blue-600">{r.score}%</td>
                    <td className="px-5 py-3.5 text-center">
                      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${
                        r.passed ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
                      }`}>
                        {r.passed
                          ? <><RiCheckLine className="w-3 h-3" /> Passed</>
                          : <><RiCloseLine className="w-3 h-3" /> Failed</>}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResponseManagement;
